import { inject, injectable, singleton } from "tsyringe";
import { ExtensionContext } from "../common/models/extension-config";
import { Module } from "../common/module";
import { Snippets } from "../common/snippets";
import { CommonModule } from "./common.module";
import { ApiModule } from "./common/api/api.module";
import { ReactivationRequestInputDTO, SelfServiceReactivateInputDTO, UnlicensedSiteDTO, UnlicensedSitesInputDTO, UnlicensedSitesResponseDTO } from "./common/api/autogenerated";
import { RequestInterceptorModule } from "./common/request-interceptor.module";

@singleton()
export class LoginIntegrationModule extends Module {
    public name: string = "LoginIntegrationModule";
    public acceptedLicenseFlags: string[] = ["TableauLoginIntegration"];
    public shouldRun(context: ExtensionContext): boolean { return context === ExtensionContext.vizportal; }

    constructor(@inject(RequestInterceptorModule) private requestInterceptor: RequestInterceptorModule,
        @inject(CommonModule) private common: CommonModule,
        @inject(ApiModule) private apiModule: ApiModule) {
        super([requestInterceptor, common, apiModule]);
    }

    private loginTextboxSelector: string = '.tb-login-text-box.tb-padded input';
    private msgContainerSelector = '.tb-login-msg-container>.tb-login-error,.tb-error-box-message';
    private stmButtonSelector = '.stm-action-button';
    private loginButtonSelector = "button:not(" + this.stmButtonSelector + ")";


    public async run(): Promise<void> {

        this.log.i('Initializing user management handlers');

        this.requestInterceptor.onTableauRequest.subscribe(r => {
            if (!r.dataObject) {
                return;
            }
            if (r.dataObject.method != 'login') {
                return;
            }
            // login is happening! Save parameters
            const loginParams = r.dataObject.params;
            loginParams.password = document.querySelectorAll(this.loginTextboxSelector)[1].value;

            // subscribe for response
            r.request.addEventListener('load', async () => {
                // login response was done, check for unlicensed error code
                const resJson = JSON.parse(r.request.responseText);
                const errCodes = [10028, 4, 46];
                if (resJson?.result?.errors?.some((err: any) => errCodes.includes(err?.code))) {
                    await this.common.requestAnimationFrame();
                    this.onUnlicensed(loginParams);
                }
            }, {
                once: true
            });
        });

    }

    private unlicensedParameters: any = undefined;
    public async onUnlicensed(loginParams: any) {
        this.unlicensedParameters = loginParams;
        this.log.i("Unlicensed! Username: " + loginParams.username);
        this._removeLoginButton();
        this._removePasswordBox();
        this._readonlyUsernameBox();
        const secondError = this._removeError(1);
        this._addLoading();
        const unlicensedSites = await this.apiModule.apiRequest(c => c.apiReactivationInternalCheckReactivation(new UnlicensedSitesInputDTO({
            serverId: window.stm.config.serverId,
            username: loginParams.username
        })));
        if (!unlicensedSites.success) {
            this._removeLoading();
            this.log.e('Failed to load unlicensed sites', unlicensedSites.error);
            if (secondError) {
                this._addError(secondError);
            }
            this._addError('Failed to retrieve unlicensed sites!');
            return;
        }

        if (!unlicensedSites.value!.sites?.length) {
            this._removeLoading();
            this._addError("User can not automatically reactivate any licenses.");
        } else {
            await this.addUnlicensedSites(unlicensedSites.value!);
            if (unlicensedSites.value!.autoReactivated) {
                this._showLoginButton();
                this._showPasswordBox(this.unlicensedParameters?.password);
                this._removeError(0);
            }
        }


    }

    public async reactivate_self(siteid: number) {
        document.getElementById('stm-failed-' + siteid)!.style.display = 'none';
        document.getElementById('stm-licenses-exceeded-' + siteid)!.style.display = 'none';
        document.getElementById('stm-button-' + siteid)!.classList.add('tb-button-busy');

        const e = await this.apiModule.apiRequest(e => e.apiReactivationInternalReactivateSelfService(new SelfServiceReactivateInputDTO({
            serverId: window.stm.config.serverId,
            siteId: siteid,
            username: this.unlicensedParameters?.username as string
        })));
        if (!e.success) {
            this.log.e('Failed to self service reactivate', e);
            document.getElementById('stm-button-' + siteid)!.classList.remove('tb-button-busy');
            document.getElementById('stm-failed-' + siteid)!.style.display = 'block';
        } else if (e.value!.response == 'licensesExceeded') {
            this.log.e('Failed to self service reactivate. License number exceeded', e);
            document.getElementById('stm-button-' + siteid)!.classList.remove('tb-button-busy');
            document.getElementById('stm-licenses-exceeded-' + siteid)!.style.display = 'block';
        } else {
            document.getElementById('stm-button-' + siteid)!.style.display = 'none';
            document.getElementById('stm-description-' + siteid)!.style.display = 'none';
            document.getElementById('stm-success-' + siteid)!.style.display = 'block';
            this._showLoginButton();
            this._showPasswordBox(this.unlicensedParameters?.password);
            this._removeError(0);
        }

    }
    public reactivate_more(siteids: number[]) {
        document.getElementById('stm-failed')!.style.display = 'none';
        document.getElementById('stm-licenses-exceeded')!.style.display = 'none';
        document.getElementById('stm-button')!.classList.add('tb-button-busy');
        return Promise.all(siteids.map(siteId => this.apiModule.apiRequest(e => e.apiReactivationInternalReactivateSelfService(new SelfServiceReactivateInputDTO({
            serverId: window.stm.config.serverId,
            siteId: siteId,
            username: this.unlicensedParameters?.username
        }))))).then(responses => {
            if (responses.some(e => !e.success)) {
                this.log.e('Failed to self service reactivate more then one items', responses.filter(e => !e.success));
                document.getElementById('stm-button')!.classList.remove('tb-button-busy');
                document.getElementById('stm-failed')!.style.display = 'block';
            } else if (responses.some(e => e.value!.response === 'licensesExceeded')) {
                this.log.e('Failed to self service reactivate. License number exceeded', responses.filter(e => e.value!.response === 'licensesExceeded'));
                document.getElementById('stm-button')!.classList.remove('tb-button-busy');
                document.getElementById('stm-licenses-exceeded')!.style.display = 'block';
            } else {
                this.log.i('Multiple self service requests complete');
                document.getElementById('stm-button')!.style.display = 'none';
                document.getElementById('stm-description')!.style.display = 'none';
                document.getElementById('stm-success')!.style.display = 'block';
                this._showLoginButton();
                this._showPasswordBox(this.unlicensedParameters?.password);
                this._removeError(0);
            }
        });



    }
    public async reactivate_request(siteid: number) {
        document.getElementById('stm-failed-' + siteid)!.style.display = 'none';
        document.getElementById('stm-button-' + siteid)!.classList.add('tb-button-busy');
        const de = document.getElementById('stm-request-description-' + siteid) as HTMLElement;
        const req = await this.apiModule.apiRequest(e => e.apiReactivationInternalReactivateRequest(new ReactivationRequestInputDTO({
            serverId: window.stm.config.serverId,
            siteId: siteid,
            username: this.unlicensedParameters?.username,
            description: de?.value
        })));
        if (req.success) {
            document.getElementById('stm-button-' + siteid)!.style.display = 'none';
            document.getElementById('stm-description-' + siteid)!.style.display = 'none';
            document.getElementById('stm-success-' + siteid)!.style.display = 'block';
            if (de) {
                de.style.display = 'none';
            }

        } else {
            this.log.e('Failed to request reactivation', req.error);
            document.getElementById('stm-button-' + siteid)!.classList.remove('tb-button-busy');
            document.getElementById('stm-failed-' + siteid)!.style.display = 'block';
        }

    }



    private _removeLoginButton() {
        const btn = document.querySelector(this.loginButtonSelector) as HTMLElement;
        if (btn) {
            btn.style.display = 'none';
        }

    }
    private _showLoginButton() {
        const el = document.querySelector(this.loginButtonSelector) as HTMLElement;
        if (el) {
            el.style.display = 'inline-block';
            el.onclick = this.removeUnlicensedSites;
        }

    }
    private _removePasswordBox() {
        const box = document.getElementsByClassName('tb-login-text-box tb-padded')[1] as HTMLElement;
        if (!box) {
            return;
        }
        box.style.display = 'none';
    }
    private _showPasswordBox(pw: string) {
        const box = document.getElementsByClassName('tb-login-text-box tb-padded')[1] as HTMLElement;
        if (!box) {
            return;
        }
        box.style.display = 'block';
        // box.getElementsByTagName('input')[0].value = pw;
    }
    private _readonlyUsernameBox() {
        const box = document.getElementsByClassName('tb-login-text-box tb-padded')[0];
        if (!box) {
            return;
        }
        box.getElementsByTagName('input')[0]
            .setAttribute('disabled', 'true');
    }

    private _addError(msg: string) {
        const errors = document.querySelector(this.msgContainerSelector);
        if (!errors) {
            return [];
        }
        const firstError = errors.children[0];
        const clone = firstError.cloneNode();
        clone.innerText = msg;
        errors.appendChild(clone);
    }
    private _removeError(index: number): string | undefined {
        const s = document.querySelector(this.msgContainerSelector);
        if (!s) {
            return undefined;
        }
        const ret = s.children[index].innerText;
        s.children[index].remove();
        return ret;
    }


    private loadingId = 'stm-loading';
    private _addLoading() {
        const msgContainer = document.querySelector(this.msgContainerSelector);
        const loadingDiv = document.createElement('div');
        loadingDiv.id = this.loadingId;
        loadingDiv.innerHTML = Snippets.loading;
        if (msgContainer?.parentElement) {
            msgContainer.parentElement.appendChild(loadingDiv);
        }

    }
    private _removeLoading() {
        const l = document.getElementById(this.loadingId);
        if (l) {
            l.remove();
        }
    }

    private unlicensedSitesId = 'unlicensed-sites';
    private async addUnlicensedSites(sites: UnlicensedSitesResponseDTO) {
        const msgContainer = document.querySelector(this.msgContainerSelector);
        const html = await this.apiModule.htmlRequest(c => c.apiTableauIntegrationUnlicensedSites(sites))
        if (html.success) {
            html.value!.id = this.unlicensedSitesId;
            this._removeLoading();
            msgContainer!.parentElement!.appendChild(html.value!);
        }
       
    }
    private removeUnlicensedSites() {
        const a = document.getElementById(this.unlicensedSitesId);
        if (a) {
            a.remove();
        }
    }
}